import * as Sentry from '@sentry/react'
import React, { type ReactNode } from 'react'

interface ErrorBoundaryProps {
  children: ReactNode
}

interface ErrorBoundaryState {
  hasError: boolean
  error?: Error
  errorId?: string
}

/**
 * Error Boundary Component
 *
 * Catches React errors in the component tree and sends them to Sentry.
 * Provides a fallback UI while the error is reported.
 *
 * Usage:
 * ```tsx
 * <ErrorBoundary>
 *   <YourComponent />
 * </ErrorBoundary>
 * ```
 *
 * This is already wrapped around the entire app in main.tsx,
 * but you can also wrap individual page/route sections for more granular control.
 */
class ErrorBoundary extends React.Component<ErrorBoundaryProps, ErrorBoundaryState> {
  constructor(props: ErrorBoundaryProps) {
    super(props)
    this.state = {
      hasError: false,
      error: undefined,
      errorId: undefined,
    }
  }

  static getDerivedStateFromError(error: Error): Partial<ErrorBoundaryState> {
    // Update state so the next render will show the fallback UI
    return {
      hasError: true,
      error,
    }
  }

  componentDidCatch(error: Error, errorInfo: React.ErrorInfo) {
    // Capture the error with Sentry
    // Pass the React component stack as context
    const eventId = Sentry.captureException(error, {
      contexts: {
        react: {
          componentStack: errorInfo.componentStack,
        },
      },
      level: 'error',
      tags: {
        errorBoundary: 'true',
      },
    })

    // Store the event ID so we can display it to the user
    this.setState({ errorId: eventId as string | undefined })

    // Log to browser console for debugging during development
    console.error('React Error Boundary caught:', error, errorInfo)
  }

  render() {
    if (this.state.hasError) {
      return (
        <div
          style={{
            display: 'flex',
            flexDirection: 'column',
            justifyContent: 'center',
            alignItems: 'center',
            minHeight: '100vh',
            backgroundColor: '#f8f9fa',
            padding: '2rem',
            fontFamily: 'system-ui, -apple-system, sans-serif',
            textAlign: 'center',
          }}
        >
          <div
            style={{
              maxWidth: '500px',
              backgroundColor: 'white',
              padding: '2rem',
              borderRadius: '8px',
              boxShadow: '0 2px 8px rgba(0,0,0,0.1)',
            }}
          >
            <h1 style={{ marginBottom: '1rem', color: '#d32f2f' }}>
              Oops! Something went wrong
            </h1>

            <p style={{ marginBottom: '1.5rem', color: '#666', lineHeight: 1.6 }}>
              We've been notified of this error and our team is looking into it.
              Your work is safe.
            </p>

            {this.state.errorId && (
              <div
                style={{
                  backgroundColor: '#f5f5f5',
                  padding: '1rem',
                  borderRadius: '4px',
                  marginBottom: '1.5rem',
                  wordBreak: 'break-all',
                }}
              >
                <p style={{ margin: '0 0 0.5rem 0', fontSize: '0.85rem', color: '#999' }}>
                  Error Reference ID:
                </p>
                <p style={{ margin: 0, fontSize: '0.9rem', fontFamily: 'monospace', color: '#333' }}>
                  {this.state.errorId}
                </p>
              </div>
            )}

            <button
              onClick={() => window.location.reload()}
              style={{
                padding: '0.75rem 1.5rem',
                backgroundColor: '#1976d2',
                color: 'white',
                border: 'none',
                borderRadius: '4px',
                fontSize: '1rem',
                cursor: 'pointer',
                marginRight: '0.5rem',
                transition: 'background-color 0.2s',
              }}
              onMouseOver={(e) => {
                e.currentTarget.style.backgroundColor = '#1565c0'
              }}
              onMouseOut={(e) => {
                e.currentTarget.style.backgroundColor = '#1976d2'
              }}
            >
              Reload Page
            </button>

            <button
              onClick={() => window.location.href = '/'}
              style={{
                padding: '0.75rem 1.5rem',
                backgroundColor: 'transparent',
                color: '#1976d2',
                border: '1px solid #1976d2',
                borderRadius: '4px',
                fontSize: '1rem',
                cursor: 'pointer',
                transition: 'all 0.2s',
              }}
              onMouseOver={(e) => {
                e.currentTarget.style.backgroundColor = '#f5f5f5'
              }}
              onMouseOut={(e) => {
                e.currentTarget.style.backgroundColor = 'transparent'
              }}
            >
              Go Home
            </button>

            {import.meta.env.DEV && this.state.error && (
              <details
                style={{
                  marginTop: '2rem',
                  textAlign: 'left',
                  padding: '1rem',
                  backgroundColor: '#fff3e0',
                  borderRadius: '4px',
                  borderLeft: '4px solid #ff9800',
                }}
              >
                <summary style={{ cursor: 'pointer', fontWeight: 'bold', marginBottom: '0.5rem' }}>
                  Error Details (Development Only)
                </summary>
                <pre
                  style={{
                    overflow: 'auto',
                    fontSize: '0.85rem',
                    margin: '0.5rem 0 0 0',
                  }}
                >
                  {this.state.error.message}
                  {'\n\n'}
                  {this.state.error.stack}
                </pre>
              </details>
            )}
          </div>
        </div>
      )
    }

    return this.props.children
  }
}

export default ErrorBoundary
